import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { BugReportKind } from "@/lib/bug-report-context";
import { BugReportDialog } from "./BugReportDialog";
import { BugReportPriority } from "@/hooks/useBugReport";

export interface BugReportPrefill {
  reportKind?: BugReportKind;
  priority?: BugReportPriority;
  area?: string;
  subject?: string;
  description?: string;
  expectedBehavior?: string;
  stepsToReproduce?: string;
  sentryEventId?: string | null;
  systemErrorLogId?: string | null;
}

interface BugReportDialogContextValue {
  openReportDialog: (prefill?: BugReportPrefill) => void;
}

const BugReportDialogContext = createContext<BugReportDialogContextValue | null>(null);

export function BugReportProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<BugReportPrefill | undefined>();

  const openReportDialog = useCallback((nextPrefill?: BugReportPrefill) => {
    setPrefill(nextPrefill);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openReportDialog }), [openReportDialog]);

  return (
    <BugReportDialogContext.Provider value={value}>
      {children}
      <BugReportDialog open={open} onOpenChange={setOpen} prefill={prefill} />
    </BugReportDialogContext.Provider>
  );
}

export function useBugReportDialog() {
  const context = useContext(BugReportDialogContext);
  if (!context) {
    throw new Error("useBugReportDialog must be used within BugReportProvider");
  }

  return context;
}
